import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import AnimateHeight from 'react-animate-height';
import styles from './styles/StateDrivenDropdown.module.scss';

type StateDrivenSlotDropdownProps = {
  isLoading?: boolean;
  isOpen: boolean;
  speed?: number;
  showActionButtons?: boolean;
  fullWidth?: boolean;
  className?: string;
  contentClassName?: string;
  buttonActionType?: 'submit' | 'button';
  buttonsLayout?: string;
  onOutsideClick?: () => void;
  onFocus?: () => void;
  onLostFocus?: () => void;
  onCancelEvent?: () => void;
  onConfirmEvent?: () => void;
  children?: React.ReactNode;
  triggerAreaSlot?: React.ReactNode;
};

const StateDrivenSlotDropdown: React.FC<StateDrivenSlotDropdownProps> = ({
  isLoading = false,
  isOpen,
  speed = 200,
  showActionButtons = false,
  fullWidth = false,
  buttonActionType = 'submit',
  buttonsLayout = '',
  className,
  contentClassName,
  onOutsideClick,
  onFocus,
  onLostFocus,
  onCancelEvent,
  onConfirmEvent,
  children,
  triggerAreaSlot,
}) => {
  const dropdownContentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      onFocus?.();
    }
  }, [isOpen, onFocus]);

  const handleFocusOut = (e: React.FocusEvent<HTMLDivElement>) => {
    const isStillFocusedWithinComponent = dropdownContentRef.current?.contains(e.relatedTarget);
    if (!isStillFocusedWithinComponent) {
      onLostFocus?.();
    }
  };

  const closeOnOutsideClick = () => {
    onOutsideClick?.();
  };

  return (
    <div
      className={classNames(styles.relative, className)}
      onBlur={handleFocusOut}
      onKeyDown={(e) => e.key === 'Escape' && onLostFocus?.()}
    >
      {isOpen && <div className={styles.overlay} onClick={closeOnOutsideClick} />}
      <div className="d-flex">{triggerAreaSlot}</div>
      <div
        id="trigger-aria"
        ref={dropdownContentRef}
        className={classNames(styles['dropdown__content'], contentClassName, {
          'w-100': fullWidth,
        })}
      >
        <AnimateHeight height={isOpen ? 'auto' : 0} duration={speed}>
          {children}
          {showActionButtons && (
            <div
              className={`${styles['filter-dropdown-nav']} d-flex align-items-center ${buttonsLayout}`}
            >
              <button
                type={buttonActionType}
                className={`btn btn-secondary ${isLoading ? 'disabled' : ''} me-2`}
                tabIndex={isOpen ? 0 : -1}
                aria-hidden={!isOpen}
                disabled={isLoading}
                onClick={onCancelEvent}
              >
                Clear
              </button>
              <button
                type={buttonActionType}
                className={`btn btn-primary ${isLoading ? 'disabled' : ''}`}
                tabIndex={isOpen ? 0 : -1}
                aria-hidden={!isOpen}
                disabled={isLoading}
                onClick={onConfirmEvent}
              >
                Apply
              </button>
            </div>
          )}
        </AnimateHeight>
      </div>
    </div>
  );
};

export default StateDrivenSlotDropdown;
